import { PureComponent } from 'react'
import _ from 'lodash'
import cx from 'classnames'
import PropTypes from 'prop-types'
import styles from './index.css'
import { PROPOSAL_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'
import CascaderWithSelectedPath from '../../../../Common/Cascader/CascaderWithSelectedPath'
import SelectedPaths from '../../../../Common/Cascader/CascaderWithSelectedPath/SelectedPaths'
import Tip from '../../Tip'
import { getCategoryTypeFlag, isNewListingProposal } from '../../../../../../lib/proposalHelper'
import { CATEGORY_TYPE, PROPOSAL_PHASE_TYPE, MARK_AS_HIDE } from '../../../../../constants/proposalTypes'
import { RadioGroup, FormError } from '../../../../Common/Form'
import { FormValueSelector } from '../../../../Common/forms'
import { getAttrTransientKey } from '../Shared/util'

const CATEGORY_VALUES_SPLIT = ','
const CATEGORY_ORDER_KEY = 'label'

const isVisiblePaths = (categoryPaths) => {
  return _.find(categoryPaths, { visible: false }) === undefined
}

const processVisible = (isPM, proposalType) => (label, visible) => {
  if (visible === false) {
    if (proposalType === 'newListing') {
      if (!isPM) {
        return null // 隱藏不顯示
      }
      return !label.includes(MARK_AS_HIDE) ? label + MARK_AS_HIDE : label
    } else { // newProduct 一律顯示(暫隱形)
      return !label.includes(MARK_AS_HIDE) ? label + MARK_AS_HIDE : label
    }
  }
  return label
}

const CascaderWithAdditionalProps = props => (
  <FormValueSelector
    selector={select => ({
      isPM: select('setting.user.identity.isPM'),
      proposalType: select('setting.proposalType')
    })}
  >
    {
      values => {
        const { isPM, proposalType } = values
        let suffixAsInvisible
        if (proposalType === PROPOSAL_TYPE.NEW_LISTING) {
          suffixAsInvisible = isPM // NewListing + 不是PM的登入者看不到隱形的選項
        } else {
          suffixAsInvisible = true // 其他遇到隱形選項都標註"(暫隱形)"
        }

        return (
          <CascaderWithSelectedPath
            {...props}
            suffixAsInvisible={suffixAsInvisible}
            processVisible={processVisible(isPM, proposalType)}
          />
        )
      }
    }
  </FormValueSelector>
)

class CategorySelector extends PureComponent {
  constructor (props) {
    super(props)
    this.hasRetrievedMismatched = {}
    this.state = {
      defaultCategoryPaths: [],
      selectedCategoryPaths: [],
      selectedCategoryValues: [],
      recentlyCategoryOptions: [],
      recentlyCategoryRadioOptions: [],
      categoryOptions: []
    }
  }

  componentDidMount () {
    const {
      subStationId,
      catId,
      catItemId,
      struDataAttrClusterId
    } = this.props

    if (subStationId) {
      this.initCascaderOptions({
        subStationId,
        catId,
        catItemId,
        struDataAttrClusterId
      })
      this.fetchRecentlyCategoryOptions(subStationId)
    }
  }

  componentWillReceiveProps (nextProps) {
    const {
      subStationId,
      catId,
      catItemId,
      struDataAttrClusterId
    } = nextProps

    if (subStationId !== this.props.subStationId) {
      this.initCascaderOptions({
        subStationId,
        catId,
        catItemId,
        struDataAttrClusterId
      })
      this.fetchRecentlyCategoryOptions(subStationId)

      /**
       * subStation 子站，
       * 一定要有！
       * 所以設置條件避免不必要的 '對 redux-from state' 的改變
       */
      if (subStationId !== '' && this.props.subStationId !== '') {
        this.onAttrClusterChange({}, [])
      }
    }
  }

  /**
   * 初始化 cat, catItem, struData 下拉選單元件中的選項，
   * 會一層一層打 category API 取得下一層的選項 list
   */
  initCascaderOptions = category => {
    const {
      subStationId,
      catId,
      catItemId,
      struDataAttrClusterId
    } = category

    this.fetchOptions({ categoryId: subStationId })
      .then(options => {
        const zoneCategory = _.find(options, { type: CATEGORY_TYPE.ZONE })
        const subStationCategory = _.get(zoneCategory, 'children[0]', {})
        const subStationCategories = _.sortBy(
          _.get(subStationCategory, 'children', []),
          CATEGORY_ORDER_KEY
        )

        // set disabled status to zone and substation type
        const zoneSubStationPaths = [{
          ...zoneCategory,
          children: [],
          disabled: true
        },
        {
          ...subStationCategory,
          children: [],
          disabled: true
        }]

        this.setState(
          () => ({
            // picker 元件的選項
            categoryOptions: subStationCategories,
            // 顯示在 `目前所在`,
            defaultCategoryPaths: zoneSubStationPaths
          }),
          () => {
            const { phase } = this.props

            /**
             * overview 階段下，如果有 mismatchedClusterId
             * 必須要把這個 cluster 的 name 設置到選擇規格表的下拉選單上
             */
            if (phase === PROPOSAL_PHASE_TYPE.OVERVIEW) {
              const { mismatchedCluster } = this.props
              const mismatchedClusterId = _.get(mismatchedCluster, 'id', null)
              if (mismatchedClusterId) {
                const mismatchedClusterName = _.get(mismatchedCluster, 'name', '')
                this.setState(() => ({
                  selectedCategoryPaths: [{ label: mismatchedClusterName }]
                }))
                return
              }
            }

            this.preloadCategories(catId, catItemId, struDataAttrClusterId)
              .then(categoryPaths => {
                /**
                   * 如果打 category 過程中，
                   * 某個 categoryId 無法取得 list，
                   * 會從 path 中被移除
                   */
                const pathLabels = this.getCategoryValuesByPaths(categoryPaths)

                /**
                   * 也就是傳入 `preloadCategories` 的 cat, catItemId, struxxId
                   * 可能跟 `categoryPaths` 不一致，
                   * 所以最後要再重新設定一次目前的 path
                   */
                this.handleChange(
                  pathLabels,
                  categoryPaths
                )
              })
          }
        )
      })
  }

  getCategoryValuesByPaths = (paths) => _.map(paths, o => o.value)
  // input will be ['cat6859', 'catItem0', '000003326630'], output will be cat6859,catItem0,000003326630
  getSerializedCategoryValue = (values) => _.join(values, CATEGORY_VALUES_SPLIT)
  // input will be cat6859,catItem0,000003326630, output will be ['cat6859', 'catItem0', '000003326630']
  getDeserializedCategoryValue = (value) => _.split(value, CATEGORY_VALUES_SPLIT)

  getCachedClusterOption = (catId, catItemId, struDataAttrClusterId) => {
    const { categoryOptions } = this.state
    const extractionFlow = _.flow(
      (o = {}) => _.find(o, { value: catId }),
      _.property('children'),
      (o = {}) => _.find(o, { value: catItemId }),
      _.property('children'),
      (o = {}) => _.find(o, { value: struDataAttrClusterId })
    )
    const cachedOption = extractionFlow(categoryOptions)
    return cachedOption
  }

  // preload category options for cascader if catId, catItemId, struDataAttrClusterId exist
  preloadCategories = async (catId, catItemId, struDataAttrClusterId) => {
    if (catId && catItemId && struDataAttrClusterId) {
      const { categoryOptions } = this.state
      // checking if there's any cached cluster option so we can skip preloading
      const cacheOption = this.getCachedClusterOption(catId, catItemId, struDataAttrClusterId)
      if (cacheOption) {
        return Promise.resolve()
      }

      try {
        // 1. using default catId to find out the option and load the layer of cat
        const firstCatOption = _.find(categoryOptions, { value: catId }) || {}
        await this.loadData([firstCatOption])

        // 2. await for the catItem options is loaded with catItem children, using default catItemId to find out the option and load the layer of cluster
        const firstCatItemOption = _.find(firstCatOption.children, { value: catItemId }) || {}
        await this.loadData([firstCatItemOption])

        // 3. await for the cluster options is loaded, pick up cluster and set as preSelectedCategoryPaths to component
        const clusterOption = _.find(firstCatItemOption.children, { value: struDataAttrClusterId }) || {}
        const preSelectedCategoryPaths = _.compact([firstCatOption, firstCatItemOption, clusterOption])

        return Promise.resolve(preSelectedCategoryPaths)
      } catch (e) {
        console.log('preloadCategories error', e)
        return []
      }
    }
  }

  loadData = (selectedOptions) => {
    // pick up the last option
    const targetOption = selectedOptions[selectedOptions.length - 1] || {}
    const {
      type,
      value: categoryId
    } = targetOption
    const categoryTypeFlag = getCategoryTypeFlag({ type })

    let finalPromise
    targetOption.loading = true
    // load options lazily
    if (!categoryTypeFlag.isCatItem) {
      // since we will only fetch single category id, so it's naturally we only take the children of first option
      finalPromise = this.fetchOptions({ categoryId, fields: ['children'] })
        .then((options) => _.get(options, '[0].children', []))
    } else {
      finalPromise = this.fetchClusterOptions(categoryId)
    }

    return finalPromise.then((options) => {
      // now that options are changed, update the options back to state
      targetOption.loading = false
      targetOption.children = _.sortBy(
        _.merge([], targetOption.children, options),
        CATEGORY_ORDER_KEY
      )
      // disable option if there's no children
      if (_.isEmpty(targetOption.children)) {
        targetOption.disabled = true
      }

      return new Promise(
        (resolve, reject) => {
          const copiedOptions = [...this.state.categoryOptions]
          this.setState(
            { categoryOptions: copiedOptions },
            () => resolve(copiedOptions)
          )
        })
    }).catch(e => {
      targetOption.loading = false
      targetOption.children = []
      return new Promise(
        (resolve, reject) => {
          this.setState(
            { categoryOptions: this.state.categoryOptions },
            () => resolve(this.state.categoryOptions)
          )
        })
    })
  }

  fetchRecentlyCategoryOptions = (subStationId) => {
    const { fetchRecentlyCategoryOptions, proposalType } = this.props
    return fetchRecentlyCategoryOptions({ categoryId: subStationId, proposalType })
      .then((resp) => resp && !resp.error ? _.get(resp, 'payload', []) : [])
      .then((options) => {
        const radioOptions = _.map(options, option => {
          const { categoryPaths, categoryValues } = option
          const { isPM } = this.props

          // newListing + 如果不是PM, 有任何路徑標示為暫隱形就整個都隱藏起來(也不允許點擊操作)
          if (isNewListingProposal(proposalType) &&
            !isPM &&
            !isVisiblePaths(categoryPaths)) {
            return null
          }

          return {
            label: (
              <SelectedPaths
                className={styles.rencentlyCategoryPath}
                selectedOptions={categoryPaths}
                processVisible={processVisible(isPM, proposalType)}
              />
            ),
            value: categoryValues
          }
        })
        this.setState(() => ({
          recentlyCategoryOptions: options,
          recentlyCategoryRadioOptions: _.compact(radioOptions)
        }))
      })
  }

  fetchClusterOptions = categoryId => {
    const {
      proposalType,
      fetchStruDataAttrClusterOptions
    } = this.props

    return fetchStruDataAttrClusterOptions({ categoryId, proposalType })
      .then((resp) => resp && !resp.error ? _.get(resp, 'payload', []) : [])
  }

  fetchOptions = params => {
    const {
      categoryId,
      fields = ['children', 'parents']
    } = params

    const {
      proposalType,
      fetchCategoryOptions
    } = this.props

    return fetchCategoryOptions({ categoryId, fields, proposalType })
      .then(resp => resp && !resp.error
        ? _.get(resp, 'payload', [])
        : []
      )
  }

  /**
   * callback when finishing cascader select,
   * @see http://react-component.github.io/cascader/
   *
   * @params {Array} selectedPathsLabels value will be sth like `["cat4796", "catItem15086", "000003326692"]`
   * @params {Array} selectedOptions will sth like `[{...},{...},{...}]`
   */
  handleChange = (selectedPathsLabels = [], selectedOptions = [], isRecentlyStatus = false) => {
    this.setState(() => ({
      selectedCategoryValues: selectedPathsLabels,
      selectedCategoryPaths: selectedOptions
    }))
    const { recentlyCategoryOptions } = this.state
    const { triggerFieldChange, onRecentlyAttributesChange } = this.props
    // if cat is gift, catItem "isGift" must be "true"
    // 所以選中的 cat 或 catItem 有 isGift 為 true，就是贈品
    const isSelectedOptionsGift = _.find(selectedOptions, { isGift: true })
    triggerFieldChange('setting.isSelectedOptionsGift', !!isSelectedOptionsGift)

    const selectedLeafOption = _.find(selectedOptions, { isLeaf: true })
    if (selectedLeafOption) {
      const selectedClusterId = _.get(selectedLeafOption, 'value', '')
      const {
        subStationId,
        mismatchedCluster
      } = this.props

      const mismatchedClusterId = _.get(mismatchedCluster, 'id', null)
      /**
       * 在有 mismatchClusterId 下，
       * 若 user 選中相同的 cluster ，
       * 把 mismatchedTransientAttribute 複製到 `__transient__` 中，
       * 視為 user 目前的規格表
       */
      if (selectedClusterId === mismatchedClusterId) {
        const mismatchedTransientAttribute = _.get(mismatchedCluster, 'transient', {})
        const [, catItemId, struDataClusterId] = selectedPathsLabels
        const activeTransientField = getAttrTransientKey(
          subStationId,
          catItemId,
          struDataClusterId
        )

        /**
         * 曾經拿過 mismatchedClusterTransient 的 path 就不可以再再拿，
         * 因為它自己已經是一個獨立的 transient，
         * user 可能已經在這個 path 有了新的設定，
         * 不可以再讓 mismatchedCluster 蓋掉它
         */
        if (!this.hasRetrievedMismatched[activeTransientField]) {
          triggerFieldChange(
            activeTransientField,
            mismatchedTransientAttribute
          )
          this.hasRetrievedMismatched[activeTransientField] = true
        }
      }

      this.onAttrClusterChange(selectedLeafOption, selectedOptions)
      const foundRecentlyCategoryOption = _.find( // 若選擇子站是近期規格資料，將直接帶入近期規格 否則為空
        recentlyCategoryOptions,
        { categoryValues: selectedPathsLabels }
      ) || []
      onRecentlyAttributesChange(foundRecentlyCategoryOption, isRecentlyStatus)
    }
  }

  /**
   * 改變 redux-form state 裡面 cat, catItemId, struDataAttrClusterId
   */
  onAttrClusterChange = (selectedLeafOption, selectedOptions) => {
    const { onAttrClusterChange } = this.props
    onAttrClusterChange(selectedLeafOption, selectedOptions)
  }

  handleRecentlyCategoryChange = async e => {
    const selectedCategory = _.get(e, 'target.value', '')
    const selectedCategoryValues = this.getDeserializedCategoryValue(selectedCategory)
    const { recentlyCategoryOptions } = this.state
    const foundRecentlyCategoryOption = _.find(
      recentlyCategoryOptions,
      { categoryValues: selectedCategoryValues }
    )

    if (foundRecentlyCategoryOption) {
      const {
        categoryValues,
        categoryPaths,
        catId,
        catItemId,
        struDataAttrClusterId
      } = foundRecentlyCategoryOption

      await this.preloadCategories(catId, catItemId, struDataAttrClusterId)

      this.handleChange(
        categoryValues,
        categoryPaths,
        true
      )
    }
  }

  renderRecentlyCategory = () => {
    const { recentlyCategoryRadioOptions } = this.state
    const hasRecentCategory = !_.isEmpty(recentlyCategoryRadioOptions)

    return hasRecentCategory
      ? (
        <div
          key='rencentlyCategory'
          className={styles.rencentlyCategory}
        >
          <Tip
            key='type'
            type='subTitle'
          >
            或選擇近期使用
          </Tip>
          <RadioGroup
            key='radio'
            data-role='recently-category-radio'
            className={styles.recentlyCategoryRadio}
            buttonStyle='plus'
            value='none-value'
            onChange={this.handleRecentlyCategoryChange}
            options={recentlyCategoryRadioOptions}
          />
        </div>
      )
      : null
  }

  render () {
    const {
      readOnly,
      className,
      error: clusterError,
      hasMultipleAttrCluster = false,
      mustShowFormError = true,
      'data-role': dataRole,
      hideFullCategoryPath = false
    } = this.props
    const {
      categoryOptions,
      defaultCategoryPaths,
      selectedCategoryPaths,
      selectedCategoryValues
    } = this.state

    return (
      <div
        className={cx(styles.CategorySelector, className)}
        ref={r => (this.categorySelector = r)}
        data-role={dataRole}
      >
        <div
          key='wrapper'
          data-role='category-selector-wrapper'
          className={cx(styles.categoryWrapper)}
        >
          {!hideFullCategoryPath && (
            <div
              key='path'
              data-role='full-category-path'
              className={styles.fullCategoryPath}
            >
              <Tip type='subTitle'>目前所在</Tip>
              <SelectedPaths
                className={styles.path}
                selectedOptions={defaultCategoryPaths}
              />
            </div>
          )}
          {!hasMultipleAttrCluster && (
            <CascaderWithAdditionalProps
              disabled={readOnly}
              value={selectedCategoryValues}
              options={categoryOptions}
              loadData={this.loadData}
              menuInputPlaceholder={['搜尋父類', '搜尋子類', '搜尋規格']}
              onChange={this.handleChange}
              selectedPaths={selectedCategoryPaths}
              isFilterOptions
            />
          )}
        </div>
        {!readOnly && (
          <>
            <FormError
              key='formError'
              showFormError={mustShowFormError}
              error={clusterError}
            />
            {this.renderRecentlyCategory()}
          </>
        )}
      </div>
    )
  }
}

CategorySelector.propTypes = {
  proposalType: PropTypes.string.isRequired,
  'data-role': PropTypes.string,
  subStationId: PropTypes.string,
  catId: PropTypes.string,
  catItemId: PropTypes.string,
  struDataAttrClusterId: PropTypes.string,
  onAttrClusterChange: PropTypes.func.isRequired,
  hideFullCategoryPath: PropTypes.bool
}

CategorySelector.defaultProps = {
  proposalType: PROPOSAL_TYPE.NEW_LISTING,
  onAttrClusterChange () { },
  'data-role': 'category-selector'
}

export default CategorySelector
